import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChartLineUp, Warning, CaretDown, CaretRight, ArrowClockwise } from "@phosphor-icons/react";

const KPI_DEFS = [
  ["approved_budget", "Approved budget"],
  ["revised_budget", "Revised budget"],
  ["contract_value", "Contract value"],
  ["invoiced", "Invoiced"],
  ["received", "Received"],
  ["outstanding_receivables", "Outstanding AR"],
  ["actual_cost", "Actual cost"],
  ["open_commitments", "Open commitments"],
  ["open_supplier_payables", "Supplier payables"],
  ["forecast_cost_at_completion", "Forecast at completion"],
  ["remaining_budget", "Remaining budget"],
  ["expected_profit_loss", "Expected P/L"],
];

function Kpi({ k, label, value }) {
  const isPct = k === "margin_pct" || k === "budget_utilization_pct";
  const neg = typeof value === "number" && value < 0 && ["remaining_budget", "expected_profit_loss"].includes(k);
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2.5" data-testid={`pfd-kpi-${k}`}>
      <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 font-display text-sm font-bold tabular-nums ${neg ? "text-red-700" : "text-slate-900"}`}>
        {value == null ? "—" : isPct ? `${value}%` : formatCurrency(value)}
      </div>
    </div>
  );
}

function RevisionsSection({ projectId, revisions, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [budget, setBudget] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const propose = async () => {
    if (!budget || !reason.trim()) return toast.error("Proposed budget and reason are required");
    setBusy(true);
    try {
      await api.post(`/pfd/${projectId}/budget-revisions`, {
        proposed_budget: parseFloat(budget), reason: reason.trim(),
      });
      toast.success("Budget revision proposed — awaiting approval");
      setShowForm(false); setBudget(""); setReason("");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const decide = async (revId, decision) => {
    try {
      await api.post(`/pfd/${projectId}/budget-revisions/${revId}/decide`, { decision });
      toast.success(`Revision ${decision}d`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="mt-4" data-testid="pfd-revisions-section">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Budget revisions (maker-checker)</h4>
        <Button size="sm" variant="outline" className="h-7" onClick={() => setShowForm(!showForm)} data-testid="pfd-propose-btn">
          Propose revision
        </Button>
      </div>
      {showForm && (
        <div className="mt-2 grid grid-cols-12 gap-2 rounded-md border border-blue-100 bg-blue-50/50 p-3">
          <div className="col-span-4">
            <Label className="text-[10px]">New budget (USD) *</Label>
            <Input className="h-8" type="number" step="0.01" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="pfd-revision-budget" />
          </div>
          <div className="col-span-6">
            <Label className="text-[10px]">Reason *</Label>
            <Input className="h-8" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Approved variation order VO-03" data-testid="pfd-revision-reason" />
          </div>
          <div className="col-span-2 flex items-end">
            <Button size="sm" className="h-8 w-full btn-primary-blue" disabled={busy} onClick={propose} data-testid="pfd-revision-submit">Submit</Button>
          </div>
        </div>
      )}
      {revisions.length === 0 ? (
        <div className="mt-2 rounded-md border border-dashed border-slate-200 p-3 text-center text-xs text-slate-400">
          No revisions — the approved budget is the original baseline.
        </div>
      ) : (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="py-1 pr-2">#</th><th className="py-1 pr-2">Previous</th><th className="py-1 pr-2">Proposed</th>
                <th className="py-1 pr-2">Δ</th><th className="py-1 pr-2">Reason</th><th className="py-1 pr-2">By</th>
                <th className="py-1 pr-2">Status</th><th className="py-1"></th>
              </tr>
            </thead>
            <tbody>
              {revisions.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`pfd-rev-row-${r.revision_no}`}>
                  <td className="py-1.5 pr-2 font-mono-alt">{r.revision_no}</td>
                  <td className="py-1.5 pr-2 tabular-nums">{formatCurrency(r.previous_budget)}</td>
                  <td className="py-1.5 pr-2 tabular-nums font-semibold">{formatCurrency(r.proposed_budget)}</td>
                  <td className={`py-1.5 pr-2 tabular-nums ${r.delta < 0 ? "text-red-700" : "text-emerald-700"}`}>{r.delta > 0 ? "+" : ""}{formatCurrency(r.delta)}</td>
                  <td className="py-1.5 pr-2 max-w-[220px] truncate" title={r.reason}>{r.reason}</td>
                  <td className="py-1.5 pr-2 text-slate-500">{(r.proposed_by || "").split("@")[0]}</td>
                  <td className="py-1.5 pr-2">
                    <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ${
                      r.status === "approved" ? "bg-emerald-50 text-emerald-700"
                      : r.status === "rejected" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="py-1.5">
                    {r.status === "pending" && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => decide(r.id, "approve")} data-testid={`pfd-rev-approve-${r.revision_no}`}>Approve</Button>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] text-red-700" onClick={() => decide(r.id, "reject")} data-testid={`pfd-rev-reject-${r.revision_no}`}>Reject</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function MiniTable({ title, rows, cols, testid }) {
  return (
    <div className="rounded-md border border-slate-200" data-testid={testid}>
      <div className="border-b border-slate-100 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">{title}</div>
      {rows.length === 0 ? (
        <div className="p-3 text-center text-xs text-slate-400">None — all settled</div>
      ) : (
        <table className="w-full text-xs">
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i} className="border-t border-slate-50">
                {cols.map((c, j) => (
                  <td key={j} className={`px-3 py-1.5 ${c.right ? "text-right tabular-nums font-mono-alt" : ""}`}>{c.render(r)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DrillDown({ projectId }) {
  const [pnl, setPnl] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [account, setAccount] = useState(null);

  const load = async () => {
    try {
      const [{ data: p }, { data: l }] = await Promise.all([
        api.get(`/finance/pa/${projectId}/pnl`),
        api.get(`/finance/pa/${projectId}/ledger`, { params: { limit: 500 } }),
      ]);
      setPnl(p); setLedger(l);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!pnl) {
    return (
      <Button size="sm" variant="outline" className="h-7 mt-1" onClick={load} data-testid="pfd-drill-load">
        <ChartLineUp size={12} className="mr-1" /> Load cost breakdown &amp; ledger drill-down
      </Button>
    );
  }
  const lines = ledger?.items || [];
  const filtered = account ? lines.filter((l) => l.account_code === account) : [];
  const accounts = [
    ...(pnl.revenue || []).map((a) => ({ ...a, group: "Revenue" })),
    ...(pnl.direct_cost || []).map((a) => ({ ...a, group: "Direct cost" })),
    ...(pnl.overhead || []).map((a) => ({ ...a, group: "Overhead" })),
  ];
  return (
    <div className="mt-2 space-y-2" data-testid="pfd-drilldown">
      <div className="rounded-md border border-slate-200">
        <div className="border-b border-slate-100 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
          P&amp;L by account — click a row to see the underlying journal lines
        </div>
        <table className="w-full text-xs">
          <tbody>
            {accounts.map((a, i) => (
              <tr key={a.account_code || i}
                  className={`cursor-pointer border-t border-slate-50 hover:bg-slate-50 ${account === a.account_code ? "bg-blue-50/60" : ""}`}
                  onClick={() => setAccount(account === a.account_code ? null : a.account_code)}
                  data-testid={`pfd-drill-account-${a.account_code}`}>
                <td className="px-3 py-1.5 w-5">{account === a.account_code ? <CaretDown size={10} /> : <CaretRight size={10} />}</td>
                <td className="px-1 py-1.5"><span className="font-mono-alt text-slate-400 mr-1">{a.account_code}</span>{a.account_name}
                  <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] uppercase text-slate-500">{a.group}</span>
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums font-mono-alt">{formatCurrency(a.amount ?? 0)}</td>
              </tr>
            ))}
            {accounts.length === 0 && (
              <tr><td className="px-3 py-2 text-center text-slate-400">No posted GL activity for this project yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {account && (
        <div className="rounded-md border border-blue-200" data-testid="pfd-drill-lines">
          <div className="border-b border-blue-100 bg-blue-50/50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-blue-800">
            Journal lines · account {account} ({filtered.length})
          </div>
          <div className="max-h-56 overflow-y-auto">
            <table className="w-full text-xs">
              <tbody>
                {filtered.map((l, i) => (
                  <tr key={l.id || i} className="border-t border-slate-50">
                    <td className="px-3 py-1 font-mono-alt text-[10px] text-slate-500">{formatDate(l.date)}</td>
                    <td className="px-2 py-1 font-mono-alt text-[10px] text-slate-400">{l.entry_no}</td>
                    <td className="px-2 py-1 max-w-[260px] truncate" title={l.description}>{l.description || "—"}</td>
                    <td className="px-2 py-1 text-right tabular-nums font-mono-alt">{l.debit ? formatCurrency(l.debit) : ""}</td>
                    <td className="px-3 py-1 text-right tabular-nums font-mono-alt text-slate-500">{l.credit ? formatCurrency(l.credit) : ""}</td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td className="px-3 py-2 text-center text-slate-400">No lines for this account in the loaded window</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProjectFinanceDashboard({ projectId, projectName }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [etc, setEtc] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [{ data: d }, { data: r }] = await Promise.all([
        api.get(`/pfd/${projectId}`),
        api.get(`/pfd/${projectId}/budget-revisions`),
      ]);
      setData(d); setRevisions(r.items || []);
      setEtc(String(d.kpis.estimate_to_complete ?? 0));
      setErr("");
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { if (open) { setData(null); load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveEtc = async () => {
    try {
      await api.patch(`/pfd/${projectId}/etc`, { estimate_to_complete: parseFloat(etc || 0) });
      toast.success("Estimate-to-complete updated — forecast recalculated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const k = data?.kpis;
  return (
    <>
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(true)} data-testid={`pfd-open-${projectId}`}>
        <ChartLineUp size={12} className="mr-1" /> Finance Dashboard
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="pfd-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ChartLineUp size={18} /> Project Finance Dashboard — {projectName}
            </DialogTitle>
            <DialogDescription>
              Live from the General Ledger, AR/AP subledgers and procurement commitments · as of {data?.as_of || "…"}
              <Button size="sm" variant="ghost" className="h-6 ml-2 px-1.5" onClick={load} title="Refresh"><ArrowClockwise size={12} /></Button>
            </DialogDescription>
          </DialogHeader>
          {err && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700" data-testid="pfd-error">{err}</div>}
          {!data && !err && <div className="p-6 text-center font-mono-alt text-xs uppercase tracking-widest text-slate-400">Loading…</div>}
          {data && (
            <div>
              {/* Alerts */}
              {data.alerts.length > 0 && (
                <div className="mb-3 space-y-1.5">
                  {data.alerts.map((a) => (
                    <div key={a.code}
                         className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium ${
                           a.level === "red" ? "border-red-300 bg-red-50 text-red-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}
                         data-testid={`pfd-alert-${a.code}`}>
                      <Warning size={14} weight="fill" /> {a.message}
                    </div>
                  ))}
                </div>
              )}

              {/* KPI grid */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                {KPI_DEFS.map(([key, label]) => <Kpi key={key} k={key} label={label} value={k[key]} />)}
                <Kpi k="margin_pct" label="Project margin" value={k.margin_pct} />
                <Kpi k="budget_utilization_pct" label="Budget utilization" value={k.budget_utilization_pct} />
              </div>

              {/* ETC / EAC control */}
              <div className="mt-3 flex flex-wrap items-end gap-2 rounded-md border border-slate-200 bg-slate-50/60 p-3">
                <div>
                  <Label className="text-[10px]">Estimate to complete (PM input, USD)</Label>
                  <Input className="h-8 w-44" type="number" step="0.01" value={etc} onChange={(e) => setEtc(e.target.value)} data-testid="pfd-etc-input" />
                </div>
                <Button size="sm" className="h-8" variant="outline" onClick={saveEtc} data-testid="pfd-etc-save">Save</Button>
                <div className="text-[11px] text-slate-500">
                  Forecast at completion = actual cost ({formatCurrency(k.actual_cost)}) + open commitments ({formatCurrency(k.open_commitments)}) + ETC
                  = <b className="text-slate-800">{formatCurrency(k.forecast_cost_at_completion)}</b>
                </div>
              </div>

              <RevisionsSection projectId={projectId} revisions={revisions} onChanged={load} />

              {/* Commitments */}
              <section className="mt-4">
                <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600">Procurement &amp; subcontract commitments</h4>
                <MiniTable title={`${data.commitments.length} awarded/active record(s) · open ${formatCurrency(k.open_commitments)}`}
                  rows={data.commitments} testid="pfd-commitments"
                  cols={[
                    { render: (r) => <span className="font-mono-alt text-[10px]">{r.ref}</span> },
                    { render: (r) => <span className="max-w-[220px] truncate inline-block" title={r.title}>{r.title}</span> },
                    { render: (r) => <span className="text-slate-500">{r.counterparty || "—"}</span> },
                    { render: (r) => formatCurrency(r.value), right: true },
                    { render: (r) => <span className="text-slate-500">billed {formatCurrency(r.billed)}</span>, right: true },
                    { render: (r) => <b>{formatCurrency(r.open_commitment)}</b>, right: true },
                  ]} />
              </section>

              {/* Outstanding payments */}
              <section className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
                <MiniTable title={`Unpaid customer invoices (${data.unpaid_customer_invoices.length})`}
                  rows={data.unpaid_customer_invoices} testid="pfd-unpaid-ar"
                  cols={[
                    { render: (r) => <span className="font-mono-alt text-[10px]">{r.invoice_number}</span> },
                    { render: (r) => r.client_name },
                    { render: (r) => <span className={r.status === "overdue" ? "text-red-600 text-[10px] font-semibold uppercase" : "text-slate-400 text-[10px] uppercase"}>{r.status}</span> },
                    { render: (r) => <b>{formatCurrency(r.balance)}</b>, right: true },
                  ]} />
                <MiniTable title={`Unpaid supplier bills (${data.unpaid_supplier_bills.length})`}
                  rows={data.unpaid_supplier_bills} testid="pfd-unpaid-ap"
                  cols={[
                    { render: (r) => <span className="font-mono-alt text-[10px]">{r.bill_number || "—"}</span> },
                    { render: (r) => r.vendor_name },
                    { render: (r) => <span className={r.status === "overdue" ? "text-red-600 text-[10px] font-semibold uppercase" : "text-slate-400 text-[10px] uppercase"}>{r.status}</span> },
                    { render: (r) => <b>{formatCurrency(r.balance)}</b>, right: true },
                  ]} />
              </section>

              {/* Drill-down */}
              <section className="mt-4 pb-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Transaction-level drill-down</h4>
                <DrillDown projectId={projectId} />
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
